import React from 'react';
import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../lib/AuthContext';
import { LayoutDashboard, Package, MessageSquare, FileText, LogOut, ShieldCheck, ExternalLink, Activity, Shield, Link2, Route } from 'lucide-react';

const items = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/offers', label: 'Offers', icon: Package },
  { to: '/admin/short-links', label: 'Short Links', icon: Link2 },
  { to: '/admin/journey', label: 'Customer Journey', icon: Route },
  { to: '/admin/popups', label: 'Popups', icon: MessageSquare },
  { to: '/admin/submissions', label: 'Form Submissions', icon: FileText },
  { to: '/admin/analytics', label: 'User Analytics', icon: Activity },
  { to: '/admin/access', label: 'Access Control', icon: Shield },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden lg:flex flex-col w-64 min-h-screen bg-slate-950 text-slate-300">
          <div className="px-5 py-5 border-b border-slate-800 flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-white font-serif-display text-base">Loan Alliance</div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400">Admin</div>
            </div>
          </div>
          <nav className="flex-1 px-3 py-4 space-y-1">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`
                }
              >
                <it.icon className="w-4 h-4" /> {it.label}
              </NavLink>
            ))}
            <div className="pt-4 mt-4 border-t border-slate-800">
              <a href="/" target="_blank" rel="noopener" className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-slate-400 hover:text-white hover:bg-slate-800">
                <ExternalLink className="w-4 h-4" /> View Site
              </a>
            </div>
          </nav>
          <div className="p-4 border-t border-slate-800">
            <div className="text-xs text-slate-400 truncate mb-2">{user?.email}</div>
            <button onClick={handleLogout} className="w-full inline-flex items-center gap-2 justify-center px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-sm text-white">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </aside>
        {/* Content */}
        <div className="flex-1 min-w-0">
          <header className="lg:hidden bg-slate-950 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-40">
            <Link to="/admin" className="font-serif-display">Loan Alliance Admin</Link>
            <button onClick={handleLogout} className="text-xs text-slate-300">Logout</button>
          </header>
          <div className="lg:hidden bg-slate-900 border-b border-slate-800">
            <div className="flex overflow-x-auto">
              {items.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.end} className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap ${
                    isActive ? 'text-emerald-400 border-b-2 border-emerald-400' : 'text-slate-400'
                  }`}>
                  <it.icon className="w-4 h-4" /> {it.label}
                </NavLink>
              ))}
            </div>
          </div>
          <main className="p-5 lg:p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
